import type { RNEventType } from '../../type/react-native';

export const isReactNative = (): boolean => {
  if (typeof window === 'undefined') return false;

  return /RNWebView/.test(window.navigator.userAgent);
};

export const isIosWebview = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    /(iPod|iPhone|iPad)/.test(window.navigator.userAgent) && isReactNative()
  );
};

export const isAndroidWebview = (): boolean => {
  if (typeof window === 'undefined') return false;

  return /Android/.test(window.navigator.userAgent) && isReactNative();
};

/** 서버가 매 요청의 클라이언트 환경을 식별하는 HTTP 헤더 이름 */
export const X_CLIENT_PLATFORM_HEADER = 'X-Client-Platform';

/** 서버가 식별하는 클라이언트 환경 값 (BE X-Client-Platform 계약) */
export enum ClientPlatform {
  WEB = 'WEB',
  APP = 'APP',
}

/**
 * 현재 클라이언트가 앱 웹뷰인지 순수 웹인지 식별한다.
 * 앱 웹뷰(RNWebView UA)면 APP, 그 외(UA 없는 SSR 포함)는 WEB.
 */
export const getClientPlatform = (): ClientPlatform =>
  isReactNative() ? ClientPlatform.APP : ClientPlatform.WEB;

type PostMessageToRNType =
  | 'login'
  | 'logout'
  | 'normalPayment'
  | 'registerPayment'
  | 'datalayer'
  | 'remove-token'
  | 'authCertification'
  | 'using-brand-pay'
  | 'requestNotificationPermission'
  | 'requestInAppReview'
  | RNEventType.saveImage;
export const postMessageToRN = <T extends any>(payload: {
  type: PostMessageToRNType;
  data?: T;
}) => {
  if (typeof window === 'undefined') return false;

  if (isReactNative() && 'ReactNativeWebView' in window) {
    window.ReactNativeWebView.postMessage(JSON.stringify(payload));
  }
};

/**
 * 앱에 OS 알림 권한 요청을 위임한다 (fire-and-forget).
 * 네이티브가 권한 상태에 따라 처리한다: 미허용 → OS 권한 팝업, 거절·소진 → 설정 안내 다이얼로그, 이미 허용 → 무동작.
 * 메시지를 처리하지 못하는 구버전 앱에서는 무시되므로 쿠폰 발급 플로우에는 영향이 없다.
 */
export const requestNotificationPermission = () =>
  postMessageToRN({ type: 'requestNotificationPermission' });

/**
 * 앱에 OS In-App Review 시트 호출을 위임한다 (fire-and-forget).
 * 네이티브가 스토어 정책(노출 쿼터)에 따라 시트 노출 여부를 결정하므로 호출이 항상 화면 노출로 이어지지는 않는다.
 * 제출 여부·별점은 OS가 공개하지 않아 웹에서 알 수 없으며, 메시지를 처리하지 못하는 구버전 앱에서는 무시된다.
 */
export const requestInAppReview = () =>
  postMessageToRN({ type: 'requestInAppReview' });
